import { t } from "@bezbolisno/i18n";
import { Card } from "@bezbolisno/ui/card";
import { type ErrorComponentProps, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { reportError } from "../instrument";
import { PageHeader } from "./page-header";

/** 404 inside the shell — `notFound()` from any authed route lands here (same copy as Next's not-found.tsx). */
export function NotFoundInShell() {
  return (
    <div className="fade-in">
      <PageHeader title={t.errors.notFoundTitle} />
      <Card className="p-5 grid gap-3 max-w-lg">
        <p className="text-sm text-[color:var(--muted)]">{t.errors.notFoundBody}</p>
        <div>
          <Link to="/" className="btn">
            {t.errors.home}
          </Link>
        </div>
      </Card>
    </div>
  );
}

/** A route threw; the shell stays up. Retry re-runs the route's loader (same copy as Next's error.tsx). */
export function RouteErrorInShell({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const [eventId, setEventId] = useState<string | null>(null);
  useEffect(() => setEventId(reportError(error)), [error]);
  return (
    <div className="fade-in">
      <Card className="p-6 grid gap-3 max-w-lg" role="alert">
        <h1 className="text-lg font-semibold">{t.errors.title}</h1>
        <p className="text-sm text-[color:var(--muted)]">{t.errors.body}</p>
        {eventId ? (
          <p className="text-xs text-[color:var(--faint)]">
            {t.errors.eventId}: <code>{eventId}</code>
          </p>
        ) : null}
        {import.meta.env.DEV ? (
          <pre className="text-xs text-[color:var(--faint)] whitespace-pre-wrap">
            {String(error)}
          </pre>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              reset();
              void router.invalidate();
            }}
          >
            {t.errors.retry}
          </button>
          <Link to="/" className="btn">
            {t.errors.home}
          </Link>
        </div>
      </Card>
    </div>
  );
}

/** Outside the shell (root): the bare fallbacks. */
export function RootError({ error, reset }: ErrorComponentProps) {
  return (
    <main className="min-h-dvh grid place-items-center p-6">
      <section className="card max-w-md w-full p-8 text-center space-y-4" role="alert">
        <h1 className="text-xl font-semibold">{t.errors.title}</h1>
        <p className="text-sm text-[color:var(--muted)]">{t.errors.body}</p>
        {import.meta.env.DEV ? (
          <pre className="text-xs whitespace-pre-wrap">{String(error)}</pre>
        ) : null}
        <button type="button" className="btn btn--primary" onClick={reset}>
          {t.errors.retry}
        </button>
      </section>
    </main>
  );
}

export function RootNotFound() {
  return (
    <main className="min-h-dvh grid place-items-center p-6">
      <section className="card max-w-md w-full p-8 text-center space-y-4">
        <h1 className="text-xl font-semibold">{t.errors.notFoundTitle}</h1>
        <p className="text-sm text-[color:var(--muted)]">{t.errors.notFoundBody}</p>
        <Link to="/" className="btn btn--primary inline-flex">
          {t.errors.home}
        </Link>
      </section>
    </main>
  );
}
